import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Badge } from 'src/@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Separator } from 'src/@/components/ui/separator';
import { Switch } from 'src/@/components/ui/switch';
import { Textarea } from 'src/@/components/ui/textarea';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

// `id` e estavel e persistido no Mongo; `i18nKey` fica separado porque os ids
// sao kebab-case, incompativel com a convencao camelCase das chaves de i18n.
// Mesma disciplina do GARANTIAS do modulo imobiliario.
const FORMAS_PAGAMENTO = [
    { id: 'pix', i18nKey: 'pix', label: 'PIX' },
    { id: 'boleto', i18nKey: 'boleto', label: 'Boleto' },
    { id: 'cartao-credito', i18nKey: 'cartaoCredito', label: 'Cartão de crédito' },
    { id: 'cartao-debito', i18nKey: 'cartaoDebito', label: 'Cartão de débito' },
    { id: 'link-pagamento', i18nKey: 'linkPagamento', label: 'Link de pagamento' },
    { id: 'pagamento-na-entrega', i18nKey: 'pagamentoNaEntrega', label: 'Pagamento na entrega' },
    { id: 'transferencia', i18nKey: 'transferencia', label: 'Transferência' },
];

// Vazio ou invalido vira undefined = "sem limite". `??` (nao `||`) na leitura
// do input para preservar o valor 0.
const coerceMoney = (value) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return undefined;
    return Math.trunc(n);
};

function FormasPagamentoPicker({ selected, onChange, t }) {
    const opcoes = useMemo(
        () => FORMAS_PAGAMENTO.map((f) => ({
            ...f,
            label: t(`aiAgents.ecommerce.politicas.forma_${f.i18nKey}`, f.label),
        })),
        [t]
    );

    const toggle = (id) => {
        const atual = Array.isArray(selected) ? selected : [];
        onChange(atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]);
    };

    const lista = Array.isArray(selected) ? selected : [];

    return (
        <div className="flex flex-wrap gap-2">
            {opcoes.map((o) => {
                const active = lista.includes(o.id);
                return (
                    <button
                        key={o.id}
                        type="button"
                        onClick={() => toggle(o.id)}
                        className={`transition-all active:transform active:scale-95 ${active ? '' : 'opacity-70 hover:opacity-100'}`}
                    >
                        <Badge
                            variant="outline"
                            className={`text-xs font-medium flex items-center gap-1.5 cursor-pointer ${active
                                ? 'bg-violet-50 text-violet-700 border-violet-200'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-violet-200'
                                }`}
                        >
                            <Iconify icon={active ? 'eva:checkmark-circle-2-fill' : 'eva:radio-button-off-outline'} width={14} />
                            {o.label}
                        </Badge>
                    </button>
                );
            })}
        </div>
    );
}

export default function PoliticasSection({ aiAgent, changeModuleConfig }) {
    const { t } = useTranslation('settings');
    const politicas = aiAgent?.config?.module?.ecommerce?.politicas || {};

    const updatePoliticas = (patch) => {
        changeModuleConfig({ politicas: { ...politicas, ...patch } });
    };

    const formas = Array.isArray(politicas.formasPagamento) ? politicas.formasPagamento : [];

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:file-document-outline" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>{t('aiAgents.ecommerce.politicas.title', 'Políticas Comerciais')}</CardTitle>
                        <CardDescription>{t('aiAgents.ecommerce.politicas.subtitle', 'Pagamento, entrega, troca e garantia — o que a assistente pode afirmar ao cliente.')}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.politicas.formasLabel', 'Formas de pagamento aceitas')}</Label>
                    <FormasPagamentoPicker
                        selected={formas}
                        onChange={(v) => updatePoliticas({ formasPagamento: v })}
                        t={t}
                    />
                    <p className={HINT_CLS}>
                        {formas.length === 0
                            ? t('aiAgents.ecommerce.politicas.formasEmpty', 'Nenhuma selecionada — a assistente não vai afirmar nenhuma forma de pagamento.')
                            : t('aiAgents.ecommerce.politicas.formasCount', '{{count}} forma(s) selecionada(s).', { count: formas.length })}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.politicas.parcelamentoLabel', 'Parcelamento máximo')}</Label>
                        <Input
                            type="number"
                            min={1}
                            max={24}
                            value={politicas.parcelamentoMax ?? ''}
                            onChange={(e) => updatePoliticas({ parcelamentoMax: coerceMoney(e.target.value) })}
                            placeholder="12"
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>{t('aiAgents.ecommerce.politicas.parcelamentoHint', 'Em quantas vezes. Vazio = a assistente não fala de parcelamento.')}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.politicas.freteGratisLabel', 'Frete grátis acima de (R$)')}</Label>
                        <Input
                            type="number"
                            min={0}
                            value={politicas.freteGratisAcima ?? ''}
                            onChange={(e) => updatePoliticas({ freteGratisAcima: coerceMoney(e.target.value) })}
                            placeholder="199"
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>{t('aiAgents.ecommerce.politicas.freteGratisHint', 'Vazio = não há frete grátis por valor.')}</p>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.politicas.prazoLabel', 'Prazo de entrega padrão')}</Label>
                    <Input
                        value={politicas.prazoEntregaPadrao || ''}
                        onChange={(e) => updatePoliticas({ prazoEntregaPadrao: e.target.value })}
                        placeholder={t('aiAgents.ecommerce.politicas.prazoPlaceholder', 'De 3 a 7 dias úteis para a capital')}
                        className={INPUT_CLS}
                    />
                </div>

                <Separator className="bg-gray-200/60" />

                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50/60">
                        <div>
                            <p className="text-sm font-medium text-gray-800">{t('aiAgents.ecommerce.politicas.retiradaLabel', 'Retirada na loja')}</p>
                            <p className={HINT_CLS}>{t('aiAgents.ecommerce.politicas.retiradaHint', 'A assistente pode oferecer retirada como alternativa ao frete.')}</p>
                        </div>
                        <Switch
                            checked={!!politicas.retiradaLoja}
                            onCheckedChange={(v) => updatePoliticas({ retiradaLoja: v })}
                        />
                    </div>

                    {politicas.retiradaLoja && (
                        <Textarea
                            value={politicas.retiradaDetalhes || ''}
                            onChange={(e) => updatePoliticas({ retiradaDetalhes: e.target.value })}
                            placeholder={t('aiAgents.ecommerce.politicas.retiradaDetalhesPlaceholder', 'Onde retirar, horários e prazo para o pedido ficar disponível.')}
                            className={INPUT_CLS}
                            rows={3}
                        />
                    )}
                </div>

                <Separator className="bg-gray-200/60" />

                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.politicas.trocaLabel', 'Troca e devolução')}</Label>
                    <Textarea
                        value={politicas.trocaDevolucao || ''}
                        onChange={(e) => updatePoliticas({ trocaDevolucao: e.target.value })}
                        placeholder={t('aiAgents.ecommerce.politicas.trocaPlaceholder', 'Prazo, condições do produto e como o cliente solicita.')}
                        className={INPUT_CLS}
                        rows={4}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.politicas.garantiaLabel', 'Garantia')}</Label>
                    <Textarea
                        value={politicas.garantia || ''}
                        onChange={(e) => updatePoliticas({ garantia: e.target.value })}
                        className={INPUT_CLS}
                        rows={3}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.politicas.detalhesLabel', 'Outras condições')}</Label>
                    <Textarea
                        value={politicas.detalhesAdicionais || ''}
                        onChange={(e) => updatePoliticas({ detalhesAdicionais: e.target.value })}
                        className={INPUT_CLS}
                        rows={4}
                    />
                </div>
            </CardContent>
        </Card>
    );
}
